import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Table, Tag, Typography } from 'antd';
import moment from 'moment';
import { getSubscribedUsers } from '../../data';

const { Title, Text } = Typography;

const BILLING_PERIOD_LABELS = {
    monthly: 'Monthly',
    quarterly: '3 Months',
    yearly: 'Yearly',
};

const DASHLET_COLOR = '#003366';

function formatMoney(amount, currency) {
    const value = Number(amount || 0);
    const formatted = value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return currency ? `${formatted} ${currency.toUpperCase()}` : formatted;
}

function getExpiryDate(order) {
    return moment(order.created_at).add(order.duration_months || 1, 'months');
}

function isOrderActive(order) {
    if (!order.created_at) return false;
    return moment().isBefore(getExpiryDate(order));
}

function getMonthlyEquivalent(order) {
    const durationMonths = Number(order.duration_months || 1);
    return Number(order.total || 0) / durationMonths;
}

export default function Subscribers() {

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    const initialize = async () => {
        setLoading(true);
        const response = await getSubscribedUsers();
        if (response) {
            setOrders(response);
        }
        setLoading(false);
    }

    useEffect(() => {
        initialize();
    }, [])

    const currency = orders[0]?.currency || 'pkr';

    const activeOrders = useMemo(() => orders.filter(isOrderActive), [orders]);

    const financials = useMemo(() => {
        const totalRevenue = orders.reduce((sum, order) => sum + Number(order.total || 0), 0);
        const totalSubtotal = orders.reduce((sum, order) => sum + Number(order.subtotal || 0), 0);
        const totalSubscriptions = orders.length;
        const averageOrderValue = totalSubscriptions ? totalRevenue / totalSubscriptions : 0;
        const totalDiscount = Math.max(totalSubtotal - totalRevenue, 0);
        const expectedMonthlyRevenue = activeOrders.reduce((sum, order) => sum + getMonthlyEquivalent(order), 0);

        return [
            { heading: 'Total Revenue', value: formatMoney(totalRevenue, currency) },
            { heading: 'Expected Monthly Revenue', value: formatMoney(expectedMonthlyRevenue, currency) },
            { heading: 'Active Subscribers', value: activeOrders.length },
            { heading: 'Total Subscriptions', value: totalSubscriptions },
            { heading: 'Average Order Value', value: formatMoney(averageOrderValue, currency) },
            { heading: 'Total Discount Given', value: formatMoney(totalDiscount, currency) },
        ];
    }, [orders, activeOrders, currency]);

    const moduleStats = useMemo(() => {
        const map = new Map();

        activeOrders.forEach((order) => {
            const modules = Array.isArray(order.modules) ? order.modules : [];
            const durationMonths = Number(order.duration_months || 1);

            modules.forEach((module) => {
                const key = module.id ?? module.name;
                if (!map.has(key)) {
                    map.set(key, { name: module.name, subscribers: 0, monthlyRevenue: 0 });
                }
                const entry = map.get(key);
                entry.subscribers += 1;
                entry.monthlyRevenue += Number(module.line_total || 0) / durationMonths;
            });
        });

        return Array.from(map.values()).sort((a, b) => b.subscribers - a.subscribers);
    }, [activeOrders]);

    const columns = [
        {
            title: 'S.No',
            key: 'index',
            render: (text, record, index) => <Text>{index + 1}</Text>,
        },
        {
            title: 'Subscriber',
            key: 'subscriber',
            render: (text, record) => (
                <>
                    <Text strong>{record.name}</Text>
                    <br />
                    <Text type="secondary">{record.email}</Text>
                </>
            ),
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: 'Modules',
            dataIndex: 'modules',
            key: 'modules',
            render: (modules) => (
                <>
                    {(Array.isArray(modules) ? modules : []).map((module, index) => (
                        <Tag key={index}>{module.name}</Tag>
                    ))}
                </>
            ),
        },
        {
            title: 'Billing',
            key: 'billing',
            render: (text, record) => (
                <>
                    <Text>{BILLING_PERIOD_LABELS[record.billing_period] || record.billing_period}</Text>
                    <br />
                    <Text type="secondary">{record.duration_months} month(s)</Text>
                </>
            ),
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            render: (total, record) => <Text strong>{formatMoney(total, record.currency)}</Text>,
        },
        {
            title: 'Status',
            key: 'active_status',
            filters: [
                { text: 'Active', value: 'active' },
                { text: 'Expired', value: 'expired' },
            ],
            onFilter: (value, record) => (value === 'active') === isOrderActive(record),
            render: (text, record) => {
                const active = isOrderActive(record);
                return (
                    <>
                        <Tag color={active ? 'green' : 'red'}>{active ? 'Active' : 'Expired'}</Tag>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {active ? 'Expires' : 'Expired'} {getExpiryDate(record).format('DD-MMM-Y')}
                        </Text>
                    </>
                );
            },
        },
        {
            title: 'Subscribed On',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (createdAt) => <Text>{createdAt ? moment(createdAt).format('DD-MMM-Y hh:mm A') : '-'}</Text>,
        },
    ];

    return (
        <Card>
            <Title level={3}>Subscribers</Title>

            <Row gutter={[16, 18]}>
                {financials.map((card, index) => (
                    <Col span={8} key={index}>
                        <Card style={{ backgroundColor: DASHLET_COLOR }}>
                            <Title level={5} style={{ color: '#FFFFFF', margin: 0 }}>{card.heading}</Title>
                            <p style={{ color: '#FFFFFF', margin: 0, textAlign: 'right', fontSize: 18 }}>{card.value}</p>
                        </Card>
                    </Col>
                ))}
            </Row>

            <br />

            <Title level={4}>Active Subscriptions by Module</Title>
            <Row gutter={[16, 18]}>
                {moduleStats.length === 0 && !loading && (
                    <Col span={24}><Text type="secondary">No active module subscriptions found.</Text></Col>
                )}
                {moduleStats.map((module, index) => (
                    <Col span={6} key={index}>
                        <Card style={{ backgroundColor: DASHLET_COLOR }}>
                            <Title level={5} style={{ color: '#FFFFFF', margin: 0 }}>{module.name}</Title>
                            <p style={{ color: '#FFFFFF', margin: 0 }}>{module.subscribers} active subscriber{module.subscribers === 1 ? '' : 's'}</p>
                            <p style={{ color: '#FFFFFF', margin: 0, textAlign: 'right' }}>{formatMoney(module.monthlyRevenue, currency)}/mo</p>
                        </Card>
                    </Col>
                ))}
            </Row>

            <br /><br />

            <Table
                dataSource={orders}
                columns={columns}
                title={() => <Title level={3}>All Subscriptions</Title>}
                loading={loading}
                rowKey="id"
            />
        </Card>
    )
}
